import { signal } from "@preact/signals";

/** Every visual decision of the atlas redesign, expressed as a lever. The Design Lab
 *  panel edits this; the style builder and marker renderer read it. Presets bundle the
 *  levers into complete looks so variants can be compared in two clicks — including
 *  `v1`, which reproduces the production appearance so the redesign always has its
 *  baseline one switch away. */
export interface AtlasConfig {
  markers: {
    /** `atlas` = fused symbol+name markers with tiered glyphs (the redesign);
     *  `dots` = the v1 clustered count-circles. */
    mode: "atlas" | "dots";
    /** Numeral count overlaid on markers. Atlas default is off: magnitude reads from
     *  symbol tier/size, exact numbers stay in the popup and the count bar. */
    showCounts: boolean;
    /** Glyph shape for tier-1 "musical capitals" (political-map capital-star tradition). */
    capitalGlyph: "rings" | "star" | "diamond" | "dot";
    /** How many cities (by count, globally in the current data) make tier 1. */
    capitalCount: number;
    /** Minimum concert count for tier 2 (filled circle); below is tier 3 (hollow). */
    tier2Min: number;
    /** Multiplier on glyph sizes. */
    sizeScale: number;
    /** Multiplier on label sizes. */
    labelScale: number;
    /** true = a marker only renders when its name also fits (no unlabeled dots — the
     *  core fused-symbol promise). false = dots may render alone under collision. */
    labelRequired: boolean;
    /** Zoom at which tier-3 towns join the map. Below it only capitals + tier-2 cities
     *  render, which is what clears space for country names at continent zoom. */
    townMinzoom: number;
    /** Tier-1 names in spaced uppercase (atlas smallcaps voice). */
    capitalCaps: boolean;
    /** `stable` = printed-atlas behavior: each city gets a precomputed integer
     *  appears-at zoom (see placement.ts), so PANNING NEVER changes which labels show
     *  and zooming only adds. `dynamic` = raw MapLibre viewport collision (labels can
     *  swap as the map drags — kept for comparison). */
    placement: "stable" | "dynamic";
    /** Extra px padding around each label box in the stable-placement packing:
     *  higher = sparser, calmer map; lower = denser. */
    stablePadding: number;
    /** How the cities the packer SUPPRESSED at the current band are conveyed — the
     *  "there is more here once you zoom in" cue. Measured against the shipped preset,
     *  the suppressed set is most of the map (4,699 of 5,028 cities at z3, 2,355 at z6),
     *  so drawing it as markers is a non-starter; these modes draw it as *texture*,
     *  strictly subordinate to the smallest town glyph.
     *
     *  `off`   — nothing (the look shipped 2026-08-14).
     *  `wash`  — soft density field fed ONLY by suppressed cities. Because the input is
     *            the residual, it self-extinguishes: labels catching up at deeper zoom
     *            shrink the field on their own, with no zoom curve to tune. It also
     *            answers the phone case without a breakpoint — the packing is global and
     *            width-independent, so a narrow pane frames a continent from a LOWER
     *            zoom (Map.tsx derives minZoom from pane width), fewer cities are
     *            admitted there, and the field is correspondingly stronger.
     *  `stipple` — grid-capped micro-dots: sharper regional resolution than a blur can
     *            hold, at the cost of being the one mode that can wreck the hierarchy
     *            if its weight creeps up.
     *  `both`  — stipple over wash. */
    residual: "off" | "wash" | "stipple" | "both";
    /** Opacity multiplier for whichever residual cue is on. */
    residualStrength: number;
    /** Isolation exemption, px. A tier-3 town with no admitted marker within this radius
     *  is admitted BELOW `townMinzoom` anyway, label and all. The floor exists to keep
     *  villages out of a crowd; a town with no neighbor for 70px has no crowd to join,
     *  and suppressing it is what made Mumbai, Nairobi, Colombo and Manila vanish from
     *  the continental view while Europe stayed dense. ~20 cities qualify globally at
     *  z3, so they can carry full labels — the fused-symbol contract survives intact.
     *  0 disables the exemption. */
    orphanRadius: number;
  };
  basemap: {
    rivers: boolean;
    /** Width/opacity multiplier for rivers. */
    riverStrength: number;
    urban: boolean;
    /** Opacity multiplier for the urban-footprint tint. */
    urbanStrength: number;
    countryLabels: boolean;
    marineLabels: boolean;
    /** Non-concert basemap place names: full voice, dimmed background voice, or hidden. */
    gazetteer: "normal" | "dim" | "off";
    states: boolean;
  };
}

const ATLAS_BASEMAP: AtlasConfig["basemap"] = {
  rivers: true,
  riverStrength: 1,
  urban: true,
  urbanStrength: 1,
  countryLabels: true,
  marineLabels: true,
  gazetteer: "dim",
  states: false,
};

/** The production look: the 2026-08-14 atlas redesign, plus the isolation exemption and
 *  the `stipple` residual cue, both signed off 2026-08-15 after a Lab comparison against
 *  `wash` and `plain` at desktop and phone widths. */
const ATLAS_MARKERS: AtlasConfig["markers"] = {
  mode: "atlas",
  showCounts: false,
  capitalGlyph: "dot",
  capitalCount: 20,
  tier2Min: 12,
  sizeScale: 1.45,
  labelScale: 1,
  labelRequired: true,
  townMinzoom: 4,
  capitalCaps: false,
  placement: "stable",
  stablePadding: 0,
  residual: "stipple",
  residualStrength: 1,
  orphanRadius: 70,
};

export const ATLAS_PRESETS: Record<string, AtlasConfig> = {
  atlas: {
    markers: { ...ATLAS_MARKERS },
    basemap: { ...ATLAS_BASEMAP },
  },
  // `plain` is the pre-cue production look, kept one click away for the same reason `v1`
  // is: whatever ships should always be comparable against what it replaced. (There is no
  // `stipple` preset any more — it IS `atlas` now, and a duplicate would make
  // matchingPreset() report an arbitrary one of the two.)
  plain: {
    markers: { ...ATLAS_MARKERS, residual: "off" },
    basemap: { ...ATLAS_BASEMAP },
  },
  wash: {
    markers: { ...ATLAS_MARKERS, residual: "wash" },
    basemap: { ...ATLAS_BASEMAP },
  },
  v1: {
    markers: {
      mode: "dots",
      showCounts: true,
      capitalGlyph: "dot",
      capitalCount: 40,
      tier2Min: 12,
      sizeScale: 1,
      labelScale: 1,
      labelRequired: false,
      townMinzoom: 4.6,
      capitalCaps: false,
      placement: "dynamic",
      stablePadding: 6,
      residual: "off",
      residualStrength: 1,
      orphanRadius: 0,
    },
    basemap: {
      rivers: false,
      riverStrength: 1,
      urban: false,
      urbanStrength: 1,
      countryLabels: false,
      marineLabels: false,
      gazetteer: "normal",
      states: true,
    },
  },
};

const STORAGE_KEY = "atlas-config-v1";
const DEFAULT_PRESET = "atlas";

function clone(cfg: AtlasConfig): AtlasConfig {
  return { markers: { ...cfg.markers }, basemap: { ...cfg.basemap } };
}

/** Merge stored values over the default preset so a stale localStorage blob from an
 *  older lever set never produces a config with missing fields. */
function load(): AtlasConfig {
  const base = clone(ATLAS_PRESETS[DEFAULT_PRESET]);
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return base;
    const stored = JSON.parse(raw) as Partial<AtlasConfig>;
    return {
      markers: { ...base.markers, ...(stored.markers ?? {}) },
      basemap: { ...base.basemap, ...(stored.basemap ?? {}) },
    };
  } catch {
    return base;
  }
}

export const atlasConfig = signal<AtlasConfig>(load());

export function setAtlasConfig(patch: {
  markers?: Partial<AtlasConfig["markers"]>;
  basemap?: Partial<AtlasConfig["basemap"]>;
}) {
  const cur = atlasConfig.value;
  const next: AtlasConfig = {
    markers: { ...cur.markers, ...(patch.markers ?? {}) },
    basemap: { ...cur.basemap, ...(patch.basemap ?? {}) },
  };
  atlasConfig.value = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* private mode etc. — config just won't persist */
  }
}

export function applyAtlasPreset(name: string) {
  const preset = ATLAS_PRESETS[name];
  if (!preset) return;
  atlasConfig.value = clone(preset);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(atlasConfig.value));
  } catch {
    /* ignore */
  }
}

/** Which preset the current config exactly matches, or null if hand-tuned. */
export function matchingPreset(): string | null {
  const cur = JSON.stringify(atlasConfig.value);
  for (const [name, preset] of Object.entries(ATLAS_PRESETS)) {
    if (JSON.stringify(preset) === cur) return name;
  }
  return null;
}

/** The Design Lab renders when explicitly requested (?lab=1) or in dev builds; ?lab=0
 *  hides it in dev so screenshots of the plain product stay one flag away. Evaluated
 *  ONCE at module load: url-sync rewrites the query string as filters change and would
 *  otherwise strip the flag, making the panel pop in after the first state sync. */
const LAB_ENABLED = (() => {
  const lab = new URLSearchParams(window.location.search).get("lab");
  if (lab === "1") return true;
  if (lab === "0") return false;
  return import.meta.env.DEV;
})();

export function labEnabled(): boolean {
  return LAB_ENABLED;
}
